import closeBtn from 'Assets/images/close-btn.svg';
import editIcon from 'Assets/images/edit-name.svg';
import RoomPin from 'Components/RoomPin';
import { Heading } from 'Components/shared/Heading';
import { CopyComponent } from 'Components/VideoComponent/CopyComponent';
import { buildInterestMailtoLink } from 'Components/VideoComponent/VideoMenu/MyRoom/utils/interestEmail';
import { VideoTrialStatusMessage } from 'Components/VideoTrialStatusMessage';
import { VIDEO_URL } from 'Constants/env';
import { STORE_CONVERSATION, STORE_PERSON, STORE_UI } from 'Constants/stores';
import { CONFERENCE_ID_REGEXP } from 'Constants/validation';
import withRouter from 'Hocs/WithRouter';
import { debounce } from 'lodash';
import { MobXProviderContext, inject, observer } from 'mobx-react';
import * as React from 'react';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { Button, Input, Message, Popup } from 'semantic-ui-react';
import { RootStoreProps } from 'Stores/RootStore.types';
import {
  ANALYTICS_EVENT_VIDEO_UPGRADE_INTEREST_REQUESTED,
  pushToGTMDataLayer,
} from 'Utils/analytics';
import { isNullOrUndefined } from 'Utils/typeGuards';
import { Styled } from './index.styles';
import { MyRoomProps, MyRoomState } from './index.types';

class MyRoom extends React.Component<MyRoomProps, MyRoomState> {
  static contextType = MobXProviderContext;
  declare context: RootStoreProps;
  constructor(props: MyRoomProps) {
    super(props);
    this.state = {
      inputValue: null,
      showEditingMode: false,
      showErrorMessage: false,
      copied: false,
      hovered: false,
      showMorePhoneNumbers: false,
      videoPopup: null,
    };
  }

  componentDidMount() {
    const { conversation, person } = this.context;
    // Only load the personal room for users with an active trial or Pro package
    if (person.personAvaliableFeatures.video.enabled) {
      void conversation.postConferencePrivateRoom();
    }
  }

  handleInterested = () => {
    pushToGTMDataLayer(ANALYTICS_EVENT_VIDEO_UPGRADE_INTEREST_REQUESTED);
  };

  handleInput = ({ currentTarget }) => {
    const {
      activeConversationError,
      setActiveConversationError,
      setPrivateRoomNameTaken,
      privateRoomNameTaken,
    } = this.context.conversation;
    this.setState({ inputValue: currentTarget.value });
    if (activeConversationError) {
      setActiveConversationError(false);
    }
    if (privateRoomNameTaken) {
      setPrivateRoomNameTaken(false);
    }
  };

  toggleInput = () => {
    const {
      privateRoomData,
      activeConversationError,
      setActiveConversationError,
      setPrivateRoomNameTaken,
      privateRoomNameTaken,
    } = this.context.conversation;
    const { showEditingMode } = this.state;
    this.setState({
      showEditingMode: !showEditingMode,
      inputValue: privateRoomData?.id,
      showErrorMessage: false,
    });
    if (activeConversationError) {
      setActiveConversationError(false);
    }
    if (privateRoomNameTaken) {
      setPrivateRoomNameTaken(false);
    }
  };

  handleOpenVideoPopup = (conferenceId: string) =>
    debounce(() => {
      if (this.state.videoPopup && !this.state.videoPopup.closed) {
        this.state.videoPopup.focus();
      } else {
        const videoPopup = this.context.conversation.openVideoPopup(conferenceId);
        this.setState({ videoPopup: videoPopup });
      }
    }, 500);

  checkIsValid = () => {
    const { inputValue } = this.state;
    const {
      conversation: { privateRoomData },
    } = this.context;
    const data = inputValue || privateRoomData.id;
    const checkLength = data.length > 2 && data.length < 37;
    const isValid = CONFERENCE_ID_REGEXP.test(data) && checkLength;
    this.setState({ showErrorMessage: isValid ? false : true });
    return isValid;
  };

  handleUpdateRoomName = async () => {
    const {
      conversation: { updateConferencePrivateRoom, privateRoomData },
    } = this.context;
    if (this.checkIsValid()) {
      const resp = await updateConferencePrivateRoom(
        this.state.inputValue || privateRoomData?.id
      );
      if (resp?.status === 200) {
        this.setState({ showErrorMessage: false });
        this.toggleInput();
      }
    }
  };

  handleCopy = () => {
    const {
      conversation: { privateRoomData },
      ui,
    } = this.context;
    const dialNumber =
      privateRoomData?.dialInInfos?.length > 0
        ? formatPhoneNumberIntl(privateRoomData?.dialInInfos[0]?.number)
        : '';
    const pin = privateRoomData?.pin?.match(/.{1,3}/g).join(' ') || '';
    ui.copyToClipboard([
      VIDEO_URL + privateRoomData?.id + `\nDial in: ${dialNumber}\nPIN: ${pin}`,
    ]);
    this.setState({ copied: true });
  };

  handleHover = (e) => this.setState({ hovered: true });

  handleLeave = (e) => this.setState({ hovered: false });

  handleShowMorePhoneNumbers = () => {
    this.setState({
      ...this.state,
      showMorePhoneNumbers: !this.state.showMorePhoneNumbers,
    });
  };

  render() {
    const {
      inputValue,
      showEditingMode,
      showErrorMessage,
      hovered,
      copied,
    } = this.state;
    const { conversation, ui, person } = this.context;
    const { privateRoomData, privateRoomNameTaken, activeConversationError } =
      conversation;
    const { enabled: videoEnabled, expirationDate: videoExpirationDate } =
      person.personAvaliableFeatures.video;
    const dialNumber =
      privateRoomData?.dialInInfos?.length > 0
        ? formatPhoneNumberIntl(privateRoomData?.dialInInfos[0]?.number)
        : '';
    return (
      <div className="private-room-container">
        <Styled.HeaderWrapper>
          <Heading variant="h1">My Personal Room</Heading>
          {videoEnabled &&
            (!showEditingMode ? (
              <Popup
                position="left center"
                inverted
                trigger={
                  <img
                    src={editIcon}
                    className="edit-btn"
                    onClick={this.toggleInput}
                  />
                }
                content="Edit personal room name"
              />
            ) : (
              <Popup
                inverted
                position="left center"
                trigger={
                  <img
                    className="close-btn"
                    src={closeBtn}
                    onClick={this.toggleInput}
                  />
                }
                content="Close editing mode"
              />
            ))}
        </Styled.HeaderWrapper>
        {!showEditingMode ? (
          <div className="private-room">
            {videoEnabled && (
              <div className="semantic-flex-row">
                <span data-private className="title">
                  {privateRoomData?.id || 'Loading ...'}
                </span>
              </div>
            )}
            <Button
              disabled={
                !videoEnabled ||
                isNullOrUndefined(privateRoomData?.id)
              }
              id="start-meeting"
              content="START MEETING"
              className="semantic-flex-fluid"
              onClick={this.handleOpenVideoPopup(privateRoomData?.id)}
            />
            {videoEnabled && videoExpirationDate && (
              <VideoTrialStatusMessage
                message={`Your free trial will expire in ${ui.getTimeLeft(videoExpirationDate)}!`}
              />
            )}
            {!videoEnabled && (
              <VideoTrialStatusMessage message="Your free trial has expired. Upgrade your account to access this feature." />
            )}
            {videoEnabled ? (
              <div className="info">
                <div
                  className={`connection-info ${copied ? 'copied' : ''}`}
                  onMouseOver={this.handleHover}
                  onMouseLeave={this.handleLeave}
                >
                  <span className="title pointer" onClick={this.handleCopy}>
                    Connection Information
                  </span>
                  <CopyComponent
                    link={
                      VIDEO_URL +
                      privateRoomData?.id +
                      `\nDial in: ${dialNumber}\nPIN: ${privateRoomData?.pin}`
                    }
                    ui={ui}
                    hovered={hovered}
                    copied={copied}
                    handleCopy={this.handleCopy}
                  />
                </div>
                <span data-private className="video-url">
                  {VIDEO_URL + privateRoomData?.id}
                </span>
                <span data-private className="semantic-flex-row">
                  <b>Dial-in:&nbsp;</b> {dialNumber}
                </span>
                <span data-private className="semantic-flex-row">
                  <b>PIN: </b> <RoomPin pin={privateRoomData?.pin} />
                </span>
                {privateRoomData?.dialInInfos?.length > 1 && (
                  <div className="semantic-flex-column">
                    <span
                      className="toggle-more-phones"
                      onClick={this.handleShowMorePhoneNumbers}
                    >
                      {this.state.showMorePhoneNumbers
                        ? 'Less phone numbers'
                        : 'More phone numbers'}
                    </span>
                    {this.state.showMorePhoneNumbers && (
                      <div className="semantic-flex-column">
                        {privateRoomData?.dialInInfos
                          ?.slice(1)
                          .map((el, index) => (
                            <div key={`number-${el.number}-${index}`}>
                              <span className="semantic-flex-row">
                                {el.number}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="trial-box">
                <div className="title">
                  Feature available for Pro Seat Users
                </div>
                <div className="trial-box-body">
                  Want to use Video Conferencing? It&apos;s available with the Pro
                  plan. Let us know if you&apos;re interested!
                </div>
                <Button
                  id="interested-video"
                  href={buildInterestMailtoLink()}
                  content="I'm interested"
                  className="semantic-flex-fluid"
                  onClick={this.handleInterested}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="editing-mode">
            <div>Rename your personal room</div>
            <div>
              Your room name can consist only of letters, numbers, dot,
              underscore, dash, and it should be 3-36 characters.
            </div>
            <div className="semantic-flex-row">
              <Input
                data-private
                placeholder="Meeting name"
                value={inputValue !== null ? inputValue : privateRoomData?.id}
                onChange={this.handleInput}
              />
              <Button content="SAVE" onClick={this.handleUpdateRoomName} />
            </div>
            {showErrorMessage && (
              <Message error className="error-background-groups">
                <Message.Content content="Personal room must..." />
                <Message.List>
                  <Message.Item content={`Contain 3 to 36 characters`} />
                  <Message.Item content="Start with a letter" />
                  <Message.Item content='Contain only letters, numbers, underscores ("_"), and dashes ("-"), and dots (".")' />
                </Message.List>
              </Message>
            )}
            {privateRoomNameTaken && (
              <Message
                error
                className="error-background-groups name-taken"
                visible={true}
              >
                <Message.Header content="Name Already Taken" />
                <Message.Content content="Please choose another name" />
              </Message>
            )}
            {activeConversationError && (
              <Message
                error
                className="error-background-groups name-taken"
                visible={true}
              >
                <Message.Header content="Personal room is currently in use" />
                <Message.Content content="Please finish your meeting if you wish to change the name" />
              </Message>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default inject(
  STORE_CONVERSATION,
  STORE_UI,
  STORE_PERSON
)(withRouter(observer(MyRoom)));
